import React, { useEffect, useState } from 'react';
import { getContactSubmissions } from './firebase';
import './AdminPanel.css';

function AdminPanel() {
    const [submissions, setSubmissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedMessage, setSelectedMessage] = useState(null);

    useEffect(() => {
        loadSubmissions();
    }, []);

    const loadSubmissions = async () => {
        setLoading(true);
        const result = await getContactSubmissions();
        if (result.success) {
            setSubmissions(result.data);
            setError(null);
        } else {
            setError(result.error);
        }
        setLoading(false);
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return 'N/A';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusBadge = (status) => {
        const statusClass = status === 'new' ? 'status-new' : 'status-read';
        return <span className={`status-badge ${statusClass}`}>{status}</span>;
    };

    if (loading) {
        return (
            <div className="admin-panel">
                <div className="admin-container">
                    <div className="loading">
                        <div className="spinner"></div>
                        <p>Loading submissions...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="admin-panel">
                <div className="admin-container">
                    <div className="error-message">
                        <h2>Error Loading Submissions</h2>
                        <p>{error}</p>
                        <button onClick={loadSubmissions} className="btn btn-primary">
                            Retry
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="admin-panel">
            <div className="admin-container">
                <div className="admin-header">
                    <div>
                        <h1>Contact Submissions</h1>
                        <p className="admin-subtitle">
                            Manage and review all contact form submissions
                        </p>
                    </div>
                    <button onClick={loadSubmissions} className="btn btn-secondary">
                        🔄 Refresh
                    </button>
                </div>

                <div className="admin-stats">
                    <div className="stat-card">
                        <div className="stat-value">{submissions.length}</div>
                        <div className="stat-label">Total Submissions</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-value">
                            {submissions.filter(s => s.status === 'new').length}
                        </div>
                        <div className="stat-label">New Messages</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-value">
                            {submissions.filter(s => s.timestamp?.toDate &&
                                new Date().getTime() - s.timestamp.toDate().getTime() < 86400000).length}
                        </div>
                        <div className="stat-label">Last 24 Hours</div>
                    </div>
                </div>

                {submissions.length === 0 ? (
                    <div className="empty-state">
                        <div className="empty-icon">📭</div>
                        <h3>No Submissions Yet</h3>
                        <p>Contact form submissions will appear here.</p>
                    </div>
                ) : (
                    <div className="submissions-grid">
                        {submissions.map((submission) => (
                            <div
                                key={submission.id}
                                className="submission-card"
                                onClick={() => setSelectedMessage(submission)}
                            >
                                <div className="submission-header">
                                    <div>
                                        <h3>{submission.name}</h3>
                                        <p className="submission-email">{submission.email}</p>
                                    </div>
                                    {getStatusBadge(submission.status)}
                                </div>

                                <div className="submission-details">
                                    {submission.company && (
                                        <div className="detail-item">
                                            <span className="detail-icon">🏢</span>
                                            <span>{submission.company}</span>
                                        </div>
                                    )}
                                    {submission.phone && (
                                        <div className="detail-item">
                                            <span className="detail-icon">📱</span>
                                            <span>{submission.phone}</span>
                                        </div>
                                    )}
                                    <div className="detail-item">
                                        <span className="detail-icon">📅</span>
                                        <span>{formatDate(submission.timestamp)}</span>
                                    </div>
                                </div>

                                <div className="submission-message">
                                    <strong>Message:</strong>
                                    <p>{submission.message}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Message Detail Modal */}
                {selectedMessage && (
                    <div className="modal-overlay" onClick={() => setSelectedMessage(null)}>
                        <div className="modal-content message-detail" onClick={(e) => e.stopPropagation()}>
                            <button className="modal-close" onClick={() => setSelectedMessage(null)}>×</button>

                            <div className="message-detail-header">
                                <h2>Message Details</h2>
                                {getStatusBadge(selectedMessage.status)}
                            </div>

                            <div className="message-detail-body">
                                <div className="detail-row">
                                    <label>Name:</label>
                                    <span>{selectedMessage.name}</span>
                                </div>
                                <div className="detail-row">
                                    <label>Email:</label>
                                    <a href={`mailto:${selectedMessage.email}`}>{selectedMessage.email}</a>
                                </div>
                                {selectedMessage.phone && (
                                    <div className="detail-row">
                                        <label>Phone:</label>
                                        <a href={`tel:${selectedMessage.phone}`}>{selectedMessage.phone}</a>
                                    </div>
                                )}
                                {selectedMessage.company && (
                                    <div className="detail-row">
                                        <label>Company:</label>
                                        <span>{selectedMessage.company}</span>
                                    </div>
                                )}
                                <div className="detail-row">
                                    <label>Submitted:</label>
                                    <span>{formatDate(selectedMessage.timestamp)}</span>
                                </div>
                                <div className="detail-row full-width">
                                    <label>Message:</label>
                                    <p className="message-text">{selectedMessage.message}</p>
                                </div>
                            </div>

                            <div className="message-actions">
                                <a
                                    href={`mailto:${selectedMessage.email}?subject=Re: Your Project Inquiry`}
                                    className="btn btn-primary"
                                >
                                    📧 Reply via Email
                                </a>
                                {selectedMessage.phone && (
                                    <a
                                        href={`tel:${selectedMessage.phone}`}
                                        className="btn btn-secondary"
                                    >
                                        📱 Call
                                    </a>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default AdminPanel;
